"use client";

import { getSlateColorToken } from "@faire/retailer-shared/SlateMappings/getSlateColorToken";
import { IButtonElement } from "@faire/web-api/indigofair/data/IButtonElement";
import { Button } from "@faire/web/slate/Button/Button";
import { IconButton } from "@faire/web/slate/IconButton";
import { getSlateIcon } from "@faire/web/slate/SlateMappings/getSlateIcon";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import React from "react";

import { extractLinkFromActions } from "@faire/retailer/components/SDUI/__internal__/ActionHandler/extractLinkFromActions";
import { useSDUIOnClickHandler } from "@faire/retailer/components/SDUI/__internal__/ActionHandler/useSDUIOnClickHandler";
import { LoggingEventParams } from "@faire/retailer/components/SDUI/__internal__/types";
import * as styles from "@faire/retailer/components/SDUI/ComponentRenderers/Button/__internal__/styles/styles.css";
import { getSlateButtonVariant } from "@faire/retailer/components/SDUI/ComponentRenderers/Button/getSlateButtonVariant";

import {
  varButtonBackgroundColor,
  varButtonColor,
} from "./__internal__/styles/styles.css";

interface IProps {
  buttonElement: IButtonElement;
  loggingData: LoggingEventParams;
  className?: string;
  // TODO: https://fairewholesale.atlassian.net/browse/FD-217518
  // fireLegacyOnClickEvent should be removed once banners and grids are fully migrated to SDUI
  // and data pipelines and dashboards no longer need the layout element events
  fireLegacyOnClickEvent?: boolean;
}

export const SDUIButtonRenderer: React.FC<IProps> = ({
  buttonElement,
  loggingData,
  className,
  fireLegacyOnClickEvent,
}) => {
  const { icon, text, variant, on_click, style_override } = buttonElement;
  const { link, newTabLink, nonNavigationActions } = extractLinkFromActions(
    on_click ?? []
  );
  const onClickHandler = useSDUIOnClickHandler(
    nonNavigationActions,
    loggingData,
    { logSDUIClickEvent: true, logLegacyClickEvent: fireLegacyOnClickEvent }
  );
  const onClickWithLinkHandler = useSDUIOnClickHandler(on_click, loggingData, {
    logSDUIClickEvent: true,
    logLegacyClickEvent: fireLegacyOnClickEvent,
  });

  if (icon) {
    const SlateIcon = getSlateIcon(icon);
    const backgroundColor = style_override?.background_color;
    const color = icon.color_token ?? style_override?.color;
    return (
      <IconButton
        variant={getSlateButtonVariant(variant)}
        onClick={onClickWithLinkHandler}
        aria-label={text ?? ""}
        className={classNames(
          backgroundColor || color ? styles.iconButtonSyles : undefined,
          className
        )}
        style={assignInlineVars({
          [varButtonBackgroundColor]: getSlateColorToken(backgroundColor),
          [varButtonColor]: getSlateColorToken(color),
        })}
      >
        <SlateIcon color={getSlateColorToken(icon.color_token)} />
      </IconButton>
    );
  }

  return (
    <Button
      variant={getSlateButtonVariant(variant)}
      linkTo={newTabLink ?? link}
      target={newTabLink ? "_blank" : undefined}
      onClick={onClickHandler}
      className={classNames(
        style_override ? styles.buttonStyles : undefined,
        className
      )}
      style={assignInlineVars({
        [varButtonBackgroundColor]: getSlateColorToken(
          style_override?.background_color
        ),
        [varButtonColor]: getSlateColorToken(style_override?.color),
      })}
    >
      {text}
    </Button>
  );
};
